import { useQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { MetricRecord } from "../MetricRecord";

import { metricsQueryKeys } from "./metricsQueryKeys";

export interface UseMetricsBySessionOptions {
  limit?: number;
  /** Leave out metrics whose names start with this. */
  excludeNamePrefix?: string;
}

/**
 * The metrics published to a single session, less those `excludeNamePrefix`
 * leaves out, up to `limit`.
 *
 * Backs the session-detail "Metrics" tab. Unlike `useInfiniteQueryMetrics`,
 * this is a single (non-paginated) call into `v1/metrics/session/{sessionId}/`,
 * so it never returns more than the route's cap of 10,000 metrics, which also
 * applies when `limit` is omitted
 * (packages/roboto_service/src/roboto_service/routes/metrics_router.py).
 *
 * TODO(ENG-2574): support pagination in the endpoint.
 */
export function useMetricsBySession(
  sessionId: string | undefined,
  orgId: string,
  { limit, excludeNamePrefix }: UseMetricsBySessionOptions = {},
) {
  const { metricsService } = useDomainServices();

  return useQuery<MetricRecord[], Error>({
    queryKey: metricsQueryKeys.session(
      sessionId,
      orgId,
      limit,
      excludeNamePrefix,
    ),
    queryFn: ({ signal }) => {
      if (sessionId === undefined) {
        return Promise.reject(new Error("Invalid sessionId"));
      }
      return metricsService.listMetricsBySession(sessionId, {
        abortSignal: signal,
        orgId,
        limit,
        excludeNamePrefix,
      });
    },
    enabled: sessionId !== undefined && orgId !== undefined,
    staleTime: 60_000,
  });
}
